import React, {
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useContext,
} from "react";
import { useRole, useContractContext } from "../ContractContext";
import BottomPanel from "../../layout/BottomPanel";
import { ApiError } from "../../api/ApiClient";
import { captureException } from "@sentry/core";
import { useTracking, EVENTS } from "../../Tracking";
import DismissIcon from "../../shared/icons/DismissIcon";
import { useLoadContractContext } from "../../LoadContract";
import { useContractParamsContext } from "../ContractParams";
import { ContractUpdate } from "./ContractUpdate";
import {
  useUpdatesClient,
  ContractUpdatesContext,
  ContractUpdateNotificationContext,
} from "./ContractUpdates";
import ContractUpdatesModal from "./ContractUpdatesModal";
import { UpdateType } from "./UpdateType";
import { ButtonLink } from "@teamscheme/button-link";

export default function ContractUpdatesProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { token } = useContractParamsContext();
  const { applyContractUpdates, onChange } = useContractContext();
  const [contractChanged, setContractChanged] = useState(new Date());
  useEffect(() => setContractChanged(new Date()), [token]);
  const [updated, setUpdated] = useState<Date | null>(null);
  const { contractLoaded } = useLoadContractContext();
  const [lastReadUpdates, setLastReadUpdates] = useState(contractLoaded);
  const updateNotificationContext = useContext(
    ContractUpdateNotificationContext
  );
  if (!updateNotificationContext) {
    throw new Error("No ContractUpdateNotificationContext found!");
  }
  const { updates, isSubscribed, clear } = updateNotificationContext;

  const role = useRole();

  const doUpdateFields = useCallback(
    function doUpdateFields(updated) {
      setUpdated(new Date());
      applyContractUpdates((prev) =>
        prev.documentHash !== updated.documentHash ? updated : prev
      );
    },
    [applyContractUpdates]
  );

  useEffect(() => {
    for (const { contract, updateType } of updates) {
      if (contract) {
        switch (updateType) {
          case UpdateType.Fields:
            doUpdateFields(contract);
            break;
          case UpdateType.Signature:
          case UpdateType.BankIdPayment:
          case UpdateType.ContractType:
          case UpdateType.OwnershipTransfer:
            onChange(contract);
            break;
          case UpdateType.InvitationAccepted:
            applyContractUpdates((contract) => ({
              ...contract,
              invitation: {
                ...contract.invitation!,
                accepted: true,
              },
            }));
            break;
          case UpdateType.RoleSpecificInfo:
            onChange(contract);
            break;
          default:
            break;
        }
      }
    }
    if (updates.length) clear();
  }, [
    applyContractUpdates,
    doUpdateFields,
    onChange,
    updates,
    clear,
    contractChanged,
  ]);

  const { fetchUpdates, fetchContract } = useUpdatesClient();

  const [unreadChanges, setUnreadChanges] = useState<ContractUpdate[]>([]);
  const [checkUpdatesError, setCheckUpdatesError] = useState<ApiError | null>(
    null
  );
  const [checkUpdatesLoading, setCheckUpdatesLoading] = useState(false);
  const [from, setFrom] = useState<Date | null>(lastReadUpdates);

  useEffect(() => {
    setFrom(lastReadUpdates || new Date());
  }, [lastReadUpdates]);

  const checkUpdates = useCallback(
    async function (from) {
      if (!from) return;

      setCheckUpdatesLoading(true);
      try {
        const { items } = await fetchUpdates({ from });

        setUnreadChanges(items.filter((update) => update.role !== role));

        setCheckUpdatesError(null);

        return items;
      } catch (error) {
        setCheckUpdatesError(error);
        captureException(error);
      } finally {
        setCheckUpdatesLoading(false);
      }
    },
    [fetchUpdates, role]
  );

  const checkAndApplyUpdates = useCallback(
    async function () {
      const items = await checkUpdates(from);

      if (items && items.length) {
        const updatedContract = await fetchContract();

        doUpdateFields(updatedContract);
      }
    },
    [checkUpdates, doUpdateFields, fetchContract, from]
  );

  useEffect(() => {
    if (isSubscribed) checkAndApplyUpdates();
  }, [isSubscribed, checkAndApplyUpdates]);

  useEffect(() => {
    if (updated) {
      checkUpdates(from);
    }
  }, [from, checkUpdates, updated]);

  const tracking = useTracking();

  function readUpdates() {
    if (updated) setLastReadUpdates(updated);
  }

  return (
    <ContractUpdatesContext.Provider
      value={{
        isUpdated: !checkUpdatesLoading && !checkUpdatesError,
      }}
    >
      {children}
      <ContractUpdatesModal
        onRead={() => {
          readUpdates();

          tracking.event({
            ...EVENTS.CONTRACT_UPDATES_MODAL.OK,
            label: `${unreadChanges.length} updates`,
          });
        }}
        updates={unreadChanges}
      >
        {({ open }) =>
          updated !== null &&
          lastReadUpdates &&
          lastReadUpdates < updated &&
          unreadChanges.length ? (
            <BottomPanel>
              <div className="UpdatesPanel">
                <ButtonLink
                  variant="secondary"
                  onClick={(
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ) => {
                    open(event.target as HTMLButtonElement);

                    tracking.event({
                      ...EVENTS.CONTRACT_UPDATES_MODAL.OPEN,
                      label: `${unreadChanges.length} updates`,
                    });
                  }}
                >
                  Se endringer gjort i kontrakten av din motpart
                </ButtonLink>{" "}
                <button
                  onClick={() => {
                    readUpdates();

                    tracking.event({
                      ...EVENTS.CONTRACT_UPDATES_MODAL.DISMISS,
                      label: `${unreadChanges.length} updates`,
                    });
                  }}
                >
                  <DismissIcon size={14} />
                </button>
              </div>
            </BottomPanel>
          ) : null
        }
      </ContractUpdatesModal>
      {checkUpdatesError ? (
        <BottomPanel>
          <span>Kunne ikke hente endringene til kontrakten.</span>
          <ButtonLink
            variant="primary"
            onClick={checkAndApplyUpdates}
            isLoading={checkUpdatesLoading}
          >
            Prøv på nytt
          </ButtonLink>
        </BottomPanel>
      ) : null}
    </ContractUpdatesContext.Provider>
  );
}
